/** Consecutive-tap detector for touch gestures.
 *
 *  Browsers don't fire `dblclick` / triple-click for touch, so the image
 *  editor recognizes multi-taps itself for: closing a polygon marquee,
 *  finishing an open pen path, and word/line text selection. All three
 *  used the same hand-rolled logic (350 ms window, 24 screen-px radius,
 *  squared-distance compare scaled by zoom) — this is the single shared
 *  implementation.
 *
 *  A tracker is stateful and single-purpose: create one per gesture
 *  surface and call `register` on every qualifying tap. It returns the
 *  consecutive-tap count (1 = single, 2 = double, 3 = triple, …), counting
 *  up while taps stay within the time window AND screen-distance of the
 *  previous tap AND share the same `key` (used by text selection to reset
 *  when the tapped IText changes). Anything outside those bounds restarts
 *  the count at 1.
 *
 *  Pure and clock-injectable (`now`) so it unit-tests without faking time. */
export interface TapTracker {
  /** Register a tap at scene point (x, y) under `zoom`; returns the
   *  consecutive-tap count. `key` defaults to null (single-target
   *  surfaces); `now` defaults to `Date.now()` and exists for tests. */
  register(x: number, y: number, zoom: number, key?: unknown, now?: number): number;
  /** Drop all state so the next `register` starts a fresh count at 1. */
  reset(): void;
}

/** @param windowMs       max ms between taps to count as consecutive
 *  @param distScreenPx   max screen-px between taps (÷ zoom → scene px) */
export function createTapTracker(windowMs = 350, distScreenPx = 24): TapTracker {
  let count = 0;
  let lastTime = 0;
  let lastX = 0;
  let lastY = 0;
  let lastKey: unknown = null;

  return {
    register(x, y, zoom, key = null, now = Date.now()) {
      const thresh = distScreenPx / (zoom || 1);
      const dx = x - lastX;
      const dy = y - lastY;
      const near = dx * dx + dy * dy < thresh * thresh;
      if (count > 0 && key === lastKey && near && now - lastTime <= windowMs) {
        count += 1;
      } else {
        count = 1;
      }
      lastTime = now;
      lastX = x;
      lastY = y;
      lastKey = key;
      return count;
    },
    reset() {
      count = 0;
      lastTime = 0;
      lastKey = null;
    },
  };
}
